import { Link } from "react-router-dom";

import { Card } from "@/components/Card";
import { Hero } from "@/components/Hero";
import { LearnGrid } from "@/components/LearnGrid";
import { QuickInfoBar } from "@/components/QuickInfoBar";
import { SEO } from "@/components/SEO";
import { site } from "@/content/site";

export function HomePage() {
  return (
    <>
      <SEO
        title="Membertou Boxing Club - Where Warriors Train"
        description={site.description}
        exactTitle
        path="/"
      />

      {/* A. Hero */}
      <Hero
        eyebrow={
          <>
            <span
              lang="mic"
              aria-label="Ta'n Wen'ju'te'kewinu'k Etl-kina'masulti'tij — Mi'kmaw for Where Warriors Train"
              className="block font-display text-base sm:text-lg not-italic normal-case leading-snug font-medium"
              style={{ color: "#d71920", textShadow: "0 1px 2px rgba(215,25,32,0.15)", letterSpacing: "0.08em" }}
            >
              Ta&apos;n Wen&apos;ju&apos;te&apos;kewinu&apos;k
              <span className="block">Etl-kina&apos;masulti&apos;tij</span>
            </span>
            <span className="block mt-1.5 text-[0.65rem] uppercase tracking-[0.2em]" style={{ color: "#0a0a0a" }}>
              &mdash; Where Warriors Train
            </span>
          </>
        }
        title="Membertou Boxing Club"
        body={
          <p>
            Creating healthy minds, bodies, and spirits through the art and sport of boxing.
          </p>
        }
        actions={
          <>
            <Link to="/join" className="btn-primary w-full sm:w-auto justify-center">
              Join Our Club
            </Link>
            <Link to="/training" className="btn-outline w-full sm:w-auto justify-center">
              Warrior Training
            </Link>
          </>
        }
        secondary={
          <div className="flex items-center justify-center">
            <img
              src="/images/membertou-boxing-logo-black.svg"
              alt=""
              width={480}
              height={480}
              decoding="async"
              className="block w-full max-w-[300px] sm:max-w-[380px] lg:max-w-[440px] h-auto"
              style={{ opacity: 0.9 }}
            />
          </div>
        }
      />

      {/* B. Quick info bar */}
      <QuickInfoBar />

      {/* D. Training section — dark charcoal with grunge */}
      <section
        className="section texture-dark"
        aria-labelledby="training-heading"
        style={{ backgroundColor: "#151515", color: "#fff" }}
      >
        <div className="container-page">
          <span className="eyebrow">Membertou Boxing Club</span>
          <h2 id="training-heading" className="mt-3 max-w-[20ch]">
            Train Like a Warrior
          </h2>
          <p className="mt-5 text-lg text-white/80 max-w-prose">
            Every workout is built around genuine boxing methods and
            conditioning. You&apos;ll train with purpose, push your limits, and
            learn the same techniques used by fighters at every level of the
            sport.
          </p>

          <div className="mt-12 grid gap-5 sm:grid-cols-3">
            {[
              {
                title: "Real Boxing Fundamentals",
                body: "Stance, footwork, punches, combinations, and defensive technique.",
              },
              {
                title: "Strength & Conditioning",
                body: "Circuit training, interval work, speed drills, and conditioning that pushes you to be your best.",
              },
              {
                title: "Confidence & Character",
                body: "Build the kind of strength that goes beyond the gym \u2014 discipline, self-belief, and heart.",
              },
            ].map((card) => (
              <Card key={card.title} title={card.title} tone="dark">
                <p>{card.body}</p>
              </Card>
            ))}
          </div>

          <div className="mt-10">
            <Link to="/training" className="btn-secondary w-full sm:w-auto justify-center">
              See Full Training Info
            </Link>
          </div>
        </div>
      </section>

      {/* E. What You'll Learn — black with grunge */}
      <section
        className="section texture-dark"
        aria-labelledby="learn-heading"
        style={{ backgroundColor: "#0a0a0a", color: "#fff" }}
      >
        <div className="container-page">
          <span className="eyebrow">Skills</span>
          <h2 id="learn-heading" className="mt-3">
            What You&apos;ll Learn
          </h2>
          <p className="mt-4 text-white/75 max-w-prose">
            Real boxing technique alongside world-class conditioning.
          </p>
          <div className="mt-10">
            <LearnGrid />
          </div>
        </div>
      </section>

      {/* G. Final CTA */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundColor: "#d71920", color: "#fff" }}
      >
        <div className="container-page relative z-10 py-16 sm:py-20 text-center flex flex-col items-center gap-5">
          <h2 className="max-w-[20ch]">Ready to Train?</h2>
          <p className="text-lg max-w-prose" style={{ color: "rgba(255,255,255,0.9)" }}>
            Drop by for a workout or contact the club for details. No experience necessary.
          </p>
          <div className="mt-2 flex flex-col sm:flex-row w-full sm:w-auto justify-center gap-3 sm:gap-4">
            <Link to="/join" className="btn-on-red w-full sm:w-auto">
              Join Our Club
            </Link>
            <Link to="/contact" className="btn-on-red-outline w-full sm:w-auto">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
